import React, { createContext, useContext, useState, useEffect } from 'react'
import { fetchCmsCollection } from '../lib/cms'
import { COMPANY_INFO, TATTOO_STYLES, PROCESS_STEPS, GALLERY_ITEMS } from '../data'

const CmsContext = createContext(null)

export function CmsProvider({ children }) {
  const [siteSettings, setSiteSettings] = useState(COMPANY_INFO)
  const [tattooStyles, setTattooStyles] = useState(TATTOO_STYLES)
  const [processSteps, setProcessSteps] = useState(PROCESS_STEPS)
  const [galleries, setGalleries] = useState([])
  const [pages, setPages] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let isMounted = true

    async function loadAllCmsData() {
      try {
        const [
          settingsRes,
          stylesRes,
          galleriesRes,
          pagesRes,
          stepsRes
        ] = await Promise.all([
          fetchCmsCollection('site_settings').catch(() => ({ items: [] })),
          fetchCmsCollection('tattoo_styles', { sort: 'order', order: 'asc', limit: 50 }).catch(() => ({ items: [] })),
          fetchCmsCollection('galleries', { sort: 'ordering', order: 'asc', limit: 50 }).catch(() => ({ items: [] })),
          fetchCmsCollection('pages', { limit: 50 }).catch(() => ({ items: [] })),
          fetchCmsCollection('process_steps', { sort: 'order', order: 'asc', limit: 50 }).catch(() => ({ items: [] }))
        ])

        if (!isMounted) return

        // 1. Site Settings
        if (settingsRes.items && settingsRes.items.length > 0) {
          const s = settingsRes.items[0]
          const logoImg = Array.isArray(s.logo) && s.logo[0] ? s.logo[0] : (typeof s.logo === 'string' ? s.logo : COMPANY_INFO.logo)

          setSiteSettings({
            name: s.company_name || COMPANY_INFO.name,
            cvr: s.cvr || COMPANY_INFO.cvr,
            phone: s.phone || COMPANY_INFO.phone,
            phoneRaw: s.phone ? s.phone.replace(/\s+/g, '').replace(/[^\d+]/g, '') : COMPANY_INFO.phoneRaw,
            email: s.email || COMPANY_INFO.email,
            address: s.address || COMPANY_INFO.address,
            city: COMPANY_INFO.city,
            fullAddress: s.address || COMPANY_INFO.fullAddress,
            facebook: s.facebook_url || COMPANY_INFO.facebook,
            openingHours: s.opening_hours || COMPANY_INFO.openingHours,
            logo: logoImg,
            heroImage: COMPANY_INFO.heroImage,
            artistImage: COMPANY_INFO.artistImage
          })
        }

        // 2. Tattoo Styles
        if (stylesRes.items && stylesRes.items.length > 0) {
          const mappedStyles = stylesRes.items.map((st) => {
            const rawImages = Array.isArray(st.image)
              ? st.image.filter(Boolean)
              : typeof st.image === 'string' && st.image
              ? [st.image]
              : []
            const img = rawImages[0] || null
            const styleIdentifier = st.style_slug || st.style_id || st.slug?._ || st.slug || st.title?.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'style'
            return {
              id: st.id || st.style_id || styleIdentifier,
              entryId: st.id,
              styleId: st.style_id || st.id,
              slug: styleIdentifier,
              title: st.title,
              tagline: st.tagline,
              description: st.description ? st.description.replace(/<[^>]*>?/gm, '') : '',
              descriptionHtml: st.description,
              image: img || COMPANY_INFO.heroImage,
              images: rawImages.length > 0 ? rawImages : (img ? [img] : []),
              rawImages: rawImages,
              order: st.order || 0,
              buttonText: st.button_text,
              secondaryButtonText: st.secondary_button_text,
              raw: st
            }
          })
          setTattooStyles(mappedStyles)
        }

        // 3. Galleries
        if (galleriesRes.items && galleriesRes.items.length > 0) {
          setGalleries(galleriesRes.items)
        }

        // 4. Process Steps (sikrer kun unikke trin 01-04)
        if (stepsRes.items && stepsRes.items.length > 0) {
          const uniqueMap = new Map()
          // Sorter efter opdateringstidspunkt så de seneste/aktive trin prioriteres
          const sortedRaw = [...stepsRes.items].sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
          sortedRaw.forEach(st => {
            const stepKey = (st.step_number || '').trim() || String(st.order || '')
            if (stepKey && !uniqueMap.has(stepKey)) {
              uniqueMap.set(stepKey, st)
            }
          })
          const uniqueItems = Array.from(uniqueMap.values())
          const mappedSteps = uniqueItems
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((st) => ({
              step: st.step_number || (st.order ? `0${st.order}` : '01'),
              title: st.title || '',
              description: st.description ? st.description.replace(/<[^>]*>?/gm, '').trim() : '',
              descriptionHtml: st.description
            }))
          setProcessSteps(mappedSteps)
        }

        // 5. Pages
        if (pagesRes.items && pagesRes.items.length > 0) {
          setPages(pagesRes.items)
        }

      } catch (err) {
        console.warn('Error loading CMS data:', err)
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadAllCmsData()

    return () => {
      isMounted = false
    }
  }, [])

  // Helper to safely get a section with fallback (retained for safety)
  const getSection = (key, fallback = {}) => {
    return fallback
  }

  // Helper to safely get a page by slug with structured fields
  const getPage = (slug, fallback = {}) => {
    const norm = (s) => (s || '').toLowerCase().trim()
    const p = pages.find((page) => {
      const pSlug = page.page_slug || page.slug?._ || page.slug || ''
      if (pSlug === slug) return true
      if (page.id === slug) return true
      const pTitle = norm(page.title)
      if (slug === 'forside' && (pTitle.includes('forside') || pSlug.includes('docuit') || pTitle.includes('docuit') || pSlug.includes('forside'))) return true
      if (slug === 'om-docuit-ink' && (pTitle.includes('om') || pSlug.includes('om'))) return true
      if (slug === 'tatoveringer' && (pTitle.includes('tatovering') || pSlug.includes('tatovering'))) return true
      if (slug === 'galleri' && (pTitle.includes('galleri') || pSlug.includes('galleri'))) return true
      if (slug === 'booking' && (pTitle.includes('booking') || pTitle.includes('kontakt') || pSlug.includes('booking'))) return true
      return false
    })
    if (!p) return fallback

    const heroImg =
      Array.isArray(p.hero_image) && p.hero_image[0]
        ? p.hero_image[0]
        : typeof p.hero_image === 'string'
        ? p.hero_image
        : null

    const introImg =
      Array.isArray(p.intro_image) && p.intro_image[0]
        ? p.intro_image[0]
        : typeof p.intro_image === 'string'
        ? p.intro_image
        : null

    return {
      title: p.title ?? fallback.title,
      pageSlug: p.page_slug ?? slug,
      subtitle: p.subtitle ?? fallback.subtitle,
      eyebrow: p.eyebrow ?? fallback.eyebrow,
      content: p.content ?? fallback.content,

      // Hero Section
      heroTitle: p.hero_title || p.title || fallback.heroTitle || fallback.title,
      heroSubtitle: p.hero_subtitle || p.subtitle || fallback.heroSubtitle || fallback.subtitle,
      heroContent: p.hero_content || p.content || fallback.heroContent || fallback.content,
      heroImage: heroImg || fallback.heroImage,

      // Intro / About Section
      introTitle: p.intro_title || fallback.introTitle,
      introContent: p.intro_content || fallback.introContent,
      introImage: introImg || fallback.introImage,

      // Styles Section
      stylesTitle: p.styles_title || fallback.stylesTitle,
      stylesSubtitle: p.styles_subtitle || fallback.stylesSubtitle,

      // Gallery Section
      galleryTitle: p.gallery_title || fallback.galleryTitle,

      // Process Section
      processTitle: p.process_title || fallback.processTitle,
      processSubtitle: p.process_subtitle || fallback.processSubtitle,
      processStep1Title: p.process_step_1_title,
      processStep1Description: p.process_step_1_description,
      processStep2Title: p.process_step_2_title,
      processStep2Description: p.process_step_2_description,
      processStep3Title: p.process_step_3_title,
      processStep3Description: p.process_step_3_description,
      processStep4Title: p.process_step_4_title,
      processStep4Description: p.process_step_4_description,

      // CTA Banner Section
      ctaTitle: p.cta_title || fallback.ctaTitle,
      ctaSubtitle: p.cta_subtitle || fallback.ctaSubtitle,

      raw: p
    }
  }

  const value = {
    siteSettings,
    getSection,
    getPage,
    tattooStyles,
    processSteps,
    galleries,
    pages,
    loading
  }

  return <CmsContext.Provider value={value}>{children}</CmsContext.Provider>
}

export function useCms() {
  const context = useContext(CmsContext)
  if (!context) {
    throw new Error('useCms must be used within a CmsProvider')
  }
  return context
}
